import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  Loader2,
  Mail,
  Plus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateOrganizationDialog } from "@/components/create-organization-dialog";
import { InviteMemberDialog } from "@/components/invite-member-dialog";
import { cn } from "@/lib/cn";
import {
  deleteOrganization,
  listInvitations,
  listMembers,
  listOrganizations,
  revokeInvitation,
  type Invitation,
  type OrganizationWithRole,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

const ROLE_LABEL: Record<string, string> = {
  OWNER: "소유자",
  ADMIN: "관리자",
  MEMBER: "멤버",
  VIEWER: "뷰어",
};

const ROLE_BADGE: Record<string, string> = {
  OWNER: "bg-primary/10 text-primary",
  ADMIN: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  MEMBER: "bg-muted text-muted-foreground",
  VIEWER: "bg-muted text-muted-foreground",
};

export function OrganizationsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [activeOrg, setActiveOrg] = useState<OrganizationWithRole | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: listOrganizations,
  });

  const orgs = orgsQuery.data ?? [];

  return (
    <div className="space-y-6 p-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">조직</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">조직 관리</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            소속된 조직과 멤버를 확인합니다. 코드와 AI 키는 클라우드에 저장되지 않습니다.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> 새 조직
        </Button>
      </header>

      <CreateOrganizationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(org) => {
          void queryClient.invalidateQueries({ queryKey: ["organizations"] });
          setActiveOrg(org);
        }}
      />

      {orgsQuery.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
        </p>
      )}

      {!orgsQuery.isLoading && orgs.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <Building2 className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="mt-3 font-medium">소속된 조직이 없습니다</p>
          <p className="mt-1 text-sm text-muted-foreground">
            가입 시 자동으로 개인 조직이 생성됩니다. 그래도 안 보인다면 새로고침해 주세요.
          </p>
        </div>
      )}

      {!orgsQuery.isLoading && orgs.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr,3fr]">
          {/* 좌: 조직 리스트 */}
          <ul className="space-y-2">
            {orgs.map((org) => {
              const isActive = activeOrg?.id === org.id;
              return (
                <li key={org.id}>
                  <button
                    type="button"
                    onClick={() => setActiveOrg(org)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg border p-4 text-left transition-colors",
                      isActive
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:bg-accent/50",
                    )}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{org.name}</p>
                        <span
                          className={cn(
                            "rounded px-2 py-0.5 text-xs",
                            ROLE_BADGE[org.role] ?? "bg-muted text-muted-foreground",
                          )}
                        >
                          {ROLE_LABEL[org.role] ?? org.role}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {org.plan} · {org.slug}
                      </p>
                    </div>
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform",
                        isActive && "rotate-90 text-foreground",
                      )}
                      aria-hidden
                    />
                  </button>
                </li>
              );
            })}
          </ul>

          {/* 우: 선택된 조직 상세 (멤버) */}
          <div className="rounded-lg border border-border bg-card p-6">
            {activeOrg ? (
              <OrganizationDetail org={activeOrg} />
            ) : (
              <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
                왼쪽에서 조직을 선택하면 멤버 목록을 볼 수 있어요
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OrganizationDetail({
  org,
}: {
  org: OrganizationWithRole;
}): JSX.Element {
  const membersQuery = useQuery({
    queryKey: ["members", org.id],
    queryFn: () => listMembers(org.id),
  });
  const members = membersQuery.data ?? [];

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-lg font-semibold">{org.name}</h2>
        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {org.plan}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">slug: {org.slug}</p>

      <div className="mt-6">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" aria-hidden />
          멤버 {members.length}명
        </div>
        {membersQuery.isLoading && (
          <p className="text-sm text-muted-foreground">불러오는 중…</p>
        )}
        {!membersQuery.isLoading && members.length === 0 && (
          <p className="text-sm text-muted-foreground">멤버가 없습니다.</p>
        )}
        {!membersQuery.isLoading && members.length > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border">
            {members.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {m.account.name?.trim() || m.account.email}
                  </p>
                  {m.account.name && (
                    <p className="truncate text-xs text-muted-foreground">
                      {m.account.email}
                    </p>
                  )}
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded px-2 py-0.5 text-xs",
                    ROLE_BADGE[m.role] ?? "bg-muted text-muted-foreground",
                  )}
                >
                  {ROLE_LABEL[m.role] ?? m.role}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(org.role === "OWNER" || org.role === "ADMIN") && (
        <InvitationsSection org={org} />
      )}

      {org.role === "OWNER" && <DangerZoneSection org={org} />}
    </div>
  );
}

function DangerZoneSection({
  org,
}: {
  org: OrganizationWithRole;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDelete = async (): Promise<void> => {
    if (confirmation !== org.name) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteOrganization(org.id);
      void queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setConfirmation("");
    } catch (err) {
      setError(extractErrorMessage(err, "삭제에 실패했습니다."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-destructive">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        워크스페이스 삭제
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        이 워크스페이스를 영구 삭제합니다. 멤버 / 초대 / 역사 모두 사라집니다.
        프로젝트가 남아있으면 먼저 모두 삭제해야 합니다 (서버에서 차단).
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input
          type="text"
          autoComplete="off"
          placeholder={`확인을 위해 워크스페이스 이름 "${org.name}" 입력`}
          className="flex-1"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
        <Button
          type="button"
          variant="destructive"
          onClick={onDelete}
          disabled={confirmation !== org.name || submitting}
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          삭제
        </Button>
      </div>
      {error && (
        <p className="mt-2 text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}

function InvitationsSection({
  org,
}: {
  org: OrganizationWithRole;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);

  const invitationsQuery = useQuery({
    queryKey: ["invitations", org.id],
    queryFn: () => listInvitations(org.id),
  });

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(org.id, invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invitations", org.id] });
    },
    onError: (err) => {
      alert(extractErrorMessage(err, "초대 취소에 실패했습니다"));
    },
  });

  const onInvited = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["invitations", org.id] });
  };

  // pending = 미수락 + 미취소 + 미만료
  const now = Date.now();
  const all = invitationsQuery.data ?? [];
  const pending = all.filter(
    (inv) =>
      !inv.acceptedAt &&
      !inv.revokedAt &&
      new Date(inv.expiresAt).getTime() > now,
  );

  // 개인 워크스페이스는 1인 사용 — 멤버 초대 불가. 팀 워크스페이스 생성 안내 노출.
  if (org.plan === "PERSONAL") {
    return (
      <div className="mt-6 rounded-md border border-dashed border-border bg-muted/20 p-4 text-sm">
        <div className="flex items-center gap-2 font-medium">
          <UserPlus className="h-4 w-4 text-muted-foreground" aria-hidden />
          멤버 초대 — 팀 워크스페이스 전용
        </div>
        <p className="mt-2 text-muted-foreground">
          개인 워크스페이스는 혼자 쓰는 공간이에요. 팀원과 함께 쓰려면 팀 워크스페이스를 새로 만들어 보세요.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-md border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserPlus className="h-4 w-4 text-muted-foreground" aria-hidden />
          멤버 초대
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setInviteOpen(true)}
        >
          <Mail className="h-3.5 w-3.5" /> 초대 발송
        </Button>
      </div>

      <InviteMemberDialog
        open={inviteOpen}
        organizationId={org.id}
        organizationName={org.name}
        inviterRole={org.role}
        onClose={() => setInviteOpen(false)}
        onInvited={onInvited}
      />

      {invitationsQuery.isLoading ? (
        <p className="mt-3 text-xs text-muted-foreground">불러오는 중…</p>
      ) : pending.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          대기 중인 초대가 없습니다.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border rounded-md border border-border">
          {pending.map((inv) => (
            <PendingInvitationRow
              key={inv.id}
              invitation={inv}
              onRevoke={() => revokeMutation.mutate(inv.id)}
              revoking={revokeMutation.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PendingInvitationRow({
  invitation,
  onRevoke,
  revoking,
}: {
  invitation: Invitation;
  onRevoke: () => void;
  revoking: boolean;
}): JSX.Element {
  const expiresAt = new Date(invitation.expiresAt);
  const daysLeft = Math.max(
    0,
    Math.ceil((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <div className="min-w-0">
        <p className="truncate">{invitation.email}</p>
        <p className="text-xs text-muted-foreground">
          {ROLE_LABEL[invitation.role] ?? invitation.role} · {daysLeft}일 후 만료
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          if (
            confirm(`${invitation.email} 의 초대를 취소할까요?`)
          ) {
            onRevoke();
          }
        }}
        disabled={revoking}
        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        aria-label="취소"
      >
        <X className="h-4 w-4" />
      </button>
    </li>
  );
}
